import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, useInView } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { lenisStore } from "@/lib/scroll";
import Magnetic from "./Magnetic";

// Three.js (~700KB) se carga aparte y solo tras el primer paint:
// el texto del hero (LCP) se renderiza inmediatamente, el 3D entra progresivamente.
const CinematicCanvas = lazy(() => import("@/three/CinematicCanvas"));

const SEGMENTS = 12;
const SEG = 1 / SEGMENTS;

const EASE = [0.16, 1, 0.3, 1];

function ChapterOverlay({ progress, index, chapter }) {
  const start = index * SEG;
  const opacity = useTransform(
    progress,
    [start + 0.06 * SEG, start + 0.34 * SEG, start + 0.7 * SEG, start + 0.97 * SEG],
    [0, 1, 1, 0]
  );
  const y = useTransform(progress, [start + 0.06 * SEG, start + 0.34 * SEG], [48, 0]);
  const left = index % 2 === 1;

  return (
    <motion.div
      style={{ opacity }}
      className={`pointer-events-none absolute inset-y-0 ${left ? "left-[6%]" : "right-[6%]"} flex items-center`}
      data-testid={`chapter-overlay-${chapter.num}`}
    >
      <motion.div style={{ y }} className={`max-w-xl ${left ? "text-left" : "text-right"}`}>
        <p className="font-mono text-[11px] tracking-[0.35em] text-volt">
          {chapter.num} — {chapter.label}
        </p>
        <h2 className="mt-5 font-display text-4xl font-medium leading-[1.02] tracking-tighter text-white sm:text-5xl lg:text-6xl">
          {chapter.title}
        </h2>
        {chapter.sub && (
          <p className="mt-6 text-base leading-relaxed text-zinc-400 md:text-lg">{chapter.sub}</p>
        )}
        {chapter.chips?.length > 0 && (
          <div className={`mt-8 flex flex-wrap gap-2 ${left ? "" : "justify-end"}`}>
            {chapter.chips.map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] text-zinc-400"
              >
                {chip}
              </span>
            ))}
          </div>
        )}
        {chapter.stats && (
          <div className={`mt-10 grid grid-cols-2 gap-x-10 gap-y-6 ${left ? "" : "justify-items-end"}`}>
            {chapter.stats.map((s) => (
              <div key={s.label}>
                <p className="font-display text-4xl font-medium tracking-tight text-white lg:text-5xl">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{s.label}</p>
              </div>
            ))}
            <p className={`col-span-2 font-mono text-[10px] text-zinc-400 ${left ? "" : "text-right"}`}>
              {chapter.statsNote}
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function Cinematic() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const progress = useRef(0);
  const mouse = useRef({ x: 0, y: 0 });
  const inView = useInView(ref, { margin: "300px" });
  const [canvasOn, setCanvasOn] = useState(false);

  // Entrada progresiva del 3D: se monta al primer gesto del usuario
  // (scroll/mouse/touch) o automáticamente ~6s después de cargar.
  // Así el texto del hero (LCP) y sus animaciones siempre pintan primero.
  useEffect(() => {
    let done = false;
    let timer = null;
    const on = () => {
      if (done) return;
      done = true;
      setCanvasOn(true);
    };
    const arm = () => { timer = setTimeout(on, 6000); };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    window.addEventListener("scroll", on, { once: true, passive: true });
    window.addEventListener("mousemove", on, { once: true, passive: true });
    window.addEventListener("touchstart", on, { once: true, passive: true });
    return () => {
      window.removeEventListener("load", arm);
      window.removeEventListener("scroll", on);
      window.removeEventListener("mousemove", on);
      window.removeEventListener("touchstart", on);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    progress.current = v;
  });

  useEffect(() => {
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const heroO = useTransform(scrollYProgress, [0, 0.55 * SEG, 0.95 * SEG], [1, 1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.95 * SEG], [0, -90]);

  const growthO = useTransform(
    scrollYProgress,
    [8.45 * SEG, 8.75 * SEG, 8.95 * SEG, 9.15 * SEG],
    [0, 1, 1, 0]
  );
  const growthScale = useTransform(scrollYProgress, [8.45 * SEG, 9.15 * SEG], [0.92, 1.04]);

  const finO = useTransform(scrollYProgress, [9.55 * SEG, 10.1 * SEG], [0, 1]);
  const fin1 = useTransform(scrollYProgress, [9.85 * SEG, 10.25 * SEG], [0, 1]);
  const fin1Y = useTransform(scrollYProgress, [9.85 * SEG, 10.25 * SEG], [40, 0]);
  const fin2 = useTransform(scrollYProgress, [10.1 * SEG, 10.45 * SEG], [0, 1]);
  const fin2Y = useTransform(scrollYProgress, [10.1 * SEG, 10.45 * SEG], [40, 0]);
  const finCta = useTransform(scrollYProgress, [10.35 * SEG, 10.65 * SEG], [0, 1]);

  const scrollStory = () => {
    const target = window.innerHeight * 1.05;
    if (lenisStore.instance) lenisStore.instance.scrollTo(target, { duration: 1.6 });
    else window.scrollTo({ top: target, behavior: "smooth" });
  };
  const goContact = () => {
    const el = document.getElementById("contacto");
    if (!el) return;
    if (lenisStore.instance) lenisStore.instance.scrollTo(el, { duration: 1.8 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="proceso"
      ref={ref}
      className="relative"
      style={{ height: `${SEGMENTS * 100}vh` }}
      data-testid="cinematic-story"
      aria-label="Historia: de la idea al crecimiento"
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        <div className="absolute inset-0">
          {canvasOn && (
            <Suspense fallback={null}>
              <CinematicCanvas progress={progress} mouse={mouse} active={inView} />
            </Suspense>
          )}
        </div>

        {/* HERO */}
        <motion.div
          style={{ opacity: heroO, y: heroY }}
          className="pointer-events-none absolute inset-0 flex flex-col items-center px-6 pt-[13vh] text-center"
          data-testid="hero-overlay"
        >
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05, ease: EASE }}
            className="font-mono text-sm tracking-[0.35em] text-volt sm:text-base"
          >
            {t.hero.kicker}
          </motion.p>
          <h1 className="mt-6 font-display text-[13vw] font-medium leading-[0.98] tracking-tighter text-white sm:text-7xl lg:text-8xl">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.0, delay: 0.15, ease: EASE }}
              >
                {t.hero.line1}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.0, delay: 0.28, ease: EASE }}
              >
                {t.hero.line2}
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
            className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 md:text-lg"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="pointer-events-auto mt-9 flex flex-col items-center gap-4 sm:flex-row"
          >
            <Magnetic strength={0.3}>
              <button
                onClick={goContact}
                data-testid="hero-cta-primary"
                className="group flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-volt hover:text-onvolt"
              >
                {t.hero.cta1}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Magnetic>
            <Magnetic strength={0.3}>
              <button
                onClick={scrollStory}
                data-testid="hero-cta-secondary"
                className="rounded-full border border-white/20 px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:border-volt/60 hover:text-volt"
              >
                {t.hero.cta2}
              </button>
            </Magnetic>
          </motion.div>
        </motion.div>

        {/* scroll hint */}
        <motion.div
          style={{ opacity: heroO }}
          className="pointer-events-none absolute inset-x-0 bottom-8 flex flex-col items-center gap-3"
          data-testid="scroll-hint"
        >
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="font-mono text-[10px] tracking-[0.35em] text-zinc-400"
          >
            {t.hero.scroll}
          </motion.span>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, 8, 0] }}
            transition={{
              opacity: { delay: 1.2, duration: 1 },
              y: { delay: 1.2, duration: 1.8, repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <ArrowDown className="h-4 w-4 text-zinc-400" />
          </motion.div>
        </motion.div>

        {/* CHAPTERS 01–08 */}
        {t.chapters.map((ch, i) => (
          <ChapterOverlay key={ch.num} progress={scrollYProgress} index={i + 1} chapter={ch} />
        ))}

        {/* BIG GROWTH */}
        <motion.div
          style={{ opacity: growthO, scale: growthScale }}
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          data-testid="growth-big"
        >
          <p className="font-display text-[22vw] font-medium leading-none tracking-tighter text-white md:text-[17vw]">
            GROWTH<span className="text-volt">.</span>
          </p>
        </motion.div>

        {/* FINALE */}
        <motion.div
          style={{ opacity: finO }}
          className="absolute inset-0 flex flex-col items-center justify-center bg-ink px-6 text-center"
          data-testid="finale-overlay"
        >
          <p className="font-mono text-[11px] tracking-[0.5em] text-zinc-400">DEVOLUZION</p>
          <motion.h2
            style={{ opacity: fin1, y: fin1Y }}
            className="mt-8 font-display text-[11vw] font-medium leading-[1.0] tracking-tighter text-white sm:text-7xl lg:text-8xl"
          >
            {t.finale.line1}
          </motion.h2>
          <motion.h2
            style={{ opacity: fin2, y: fin2Y }}
            className="font-display text-[11vw] font-medium leading-[1.0] tracking-tighter text-white sm:text-7xl lg:text-8xl"
          >
            {t.finale.line2}
          </motion.h2>
          <motion.div style={{ opacity: finCta }} className="mt-12">
            <Magnetic strength={0.3}>
              <button
                onClick={goContact}
                data-testid="finale-cta"
                className="group flex items-center gap-3 rounded-full bg-white px-10 py-5 text-base font-medium text-black transition-colors duration-300 hover:bg-volt hover:text-onvolt"
              >
                {t.finale.cta}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
            </Magnetic>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
